import React from 'react';
import { ItineraryTypes } from '../types';
import { formatDateDisplay } from 'utils';
import { flexibleAttributesFormatter } from './common';
import { ItemMedia } from './ItemMedia';
import { VerticalSpace } from 'ui/VerticalSpace';
import { SeeMoreCollapsible } from 'ui/SeeMoreCollapsible';
import { ItemNotesAndAttachments } from './ItemNotesAndAttachments';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';

export interface iBespokeProps {
  item: ItineraryTypes['ItineraryItem'];
}

export const Bespoke = (props: iBespokeProps) => {
  const { item } = props;
  const numberOfAdults = item.numberOfAdults || 0;
  const agesOfAllChildren = item.agesOfAllChildren || [];

  const ancillaryDescription = item.ancillary_description;

  const maxPeople = item.ancillary_productRestrictions_maximumPeople;
  const minAge = item.ancillary_productRestrictions_minimumAge;
  const restrictions = item.ancillary_restrictions;

  const overview = item.ancillary_overview;
  const highlights = item.ancillary_highlights;

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 mb-10px gap-5px">
        <h2 className="m-0 font-hurmegeometric-sans uppercase text-flint text-[14px]">Experience</h2>
        <div className="hidden md:block">{/* deliberately blank */}</div>
        <span className="font-hurmegeometric-sans text-14px text-flint uppercase">{item.title}</span>
        {(numberOfAdults > 0 || agesOfAllChildren.length > 0) && (
          <span className="flex items-center gap-2 font-hurmegeometric-sans text-14px text-flint uppercase md:justify-self-end">
            {numberOfAdults > 0 && <span>{numberOfAdults} ADULT</span>}
            {agesOfAllChildren.length > 0 && <span>{agesOfAllChildren.length} CHILD</span>}

            {item.flexibleAttributes && <span>{flexibleAttributesFormatter(item.flexibleAttributes)}</span>}
          </span>
        )}
      </div>

      <ItemMedia
        vimeoVideoId={item.vimeoVideoId}
        uploads={item.uploads as ItineraryTypes['ItineraryImageUploadOrThumbnail'][]}
        titleForModal={item.title}
      />

      <VerticalSpace height="10px" />

      <SeeMoreCollapsible cutoffHeightPx={100}>
        <div className="flex flex-col gap-2">
          {ancillaryDescription && (
            <HtmlStringRenderer 
              content={ancillaryDescription}
              className="block font-hurmegeometric-sans text-14px text-flint"
              fallbackComponent="span"
            />
          )}

          <div>
            {restrictions && (
              <span className="block font-hurmegeometric-sans text-14px text-flint">Restrictions: <HtmlStringRenderer 
                content={restrictions}
                className="font-hurmegeometric-sans text-14px text-flint"
                fallbackComponent="span"
              /></span>
            )}
            {maxPeople && (
              <span className="block font-hurmegeometric-sans text-14px text-flint">Max People: {maxPeople}</span>
            )}
            {minAge && <span className="block font-hurmegeometric-sans text-14px text-flint">Min Age: {minAge}</span>}
          </div>
          {overview && (
            <div>
              <span className="block uppercase font-hurmegeometric-sans text-14px text-flint leading-22px">
                Overview:
              </span>
              <HtmlStringRenderer 
                content={overview}
                className="block font-hurmegeometric-sans text-14px text-flint leading-22px"
                fallbackComponent="span"
              />
            </div>
          )}
          {highlights && (
            <div>
              <span className="block uppercase font-hurmegeometric-sans text-14px text-flint leading-22px">
                highlights:
              </span>
              <HtmlStringRenderer 
                content={highlights}
                className="block font-hurmegeometric-sans text-14px text-flint leading-22px"
                fallbackComponent="span"
              />
            </div>
          )}
        </div>
      </SeeMoreCollapsible>

      <VerticalSpace height="10px" />

      {(item.notes.length > 0 || item.uploads.length > 0) && (
        <>
          <VerticalSpace height="10px" />
          <ItemNotesAndAttachments notes={item.notes} uploads={item.uploads} />
          <VerticalSpace height="40px" />
        </>
      )}

      {item.notes.length === 0 && item.uploads.length === 0 && (
        <>
          <VerticalSpace height="40px" />
        </>
      )}
    </div>
  );
};
